import { DateTime } from "luxon";
import { canScheduleOrder, HHMMtoDate } from "./dateTimeUtils";
import { DayPart } from "@/dto/dayPartMenu";
import { Merchant } from "@/dto/merchant";
import { Service } from "@/dto/service";
import { isEmpty, isNil } from "lodash";
import { isBusinessOpen } from "./isBusinessOpenUtil";
import { TFunction } from "@/types";
import { OrderType, ServiceType, daysOfWeek } from "@/constants/enum";
import { ScheduleDateType } from "@/dto/scheduledTime";
import { Hours } from "@/dto/hours";
import { TIME_CONSTANTS } from "./constants";

export const isWithinBusinessHours = (
  dateTime: DateTime,
  businessHours: any,
  timezone: string,
  daypartMenu: DayPart | null = null
): boolean => {
  const zonedTime = dateTime.setZone(timezone);
  const day = zonedTime.toFormat("cccc").toLowerCase();

  const businessHoursForDay = businessHours?.[day] ?? [];
  const { days: dpmDays, time: dpmTime } = daypartMenu || {};

  // If there are no business hours for the day, the business is closed.
  if (businessHoursForDay.length === 0) {
    return false;
  }

  let effectiveHours: { start: number; end: number }[] = [];

  const isDpmAvailableToday =
    dpmDays?.map((d) => d.toLowerCase()).includes(day) && dpmTime;

  if (isDpmAvailableToday) {
    // Daypart menu exists for today, so find the intersection with business hours.
    const menuEndTime = dpmTime.endTime;
    effectiveHours = businessHoursForDay
      .map((slot: any) => ({
        start: Math.max(slot.start, dpmTime.startTime),
        end: Math.min(slot.end, menuEndTime),
      }))
      .filter((slot: any) => slot.start < slot.end); // Keep only valid (non-empty) intersections
  } else if (!daypartMenu) {
    // No daypart menu was passed in, so just use the general business hours.
    effectiveHours = businessHoursForDay;
  }
  // If a daypart menu was passed but not for today, effectiveHours remains empty, and the function will return false.

  return effectiveHours.some((timeSlot) => {
    const start = DateTime.fromFormat(
      String(timeSlot.start).padStart(4, "0"),
      "HHmm",
      {
        zone: timezone,
      }
    ).set({
      year: zonedTime.year,
      month: zonedTime.month,
      day: zonedTime.day,
    });

    const endRaw = DateTime.fromFormat(
      String(timeSlot.end).padStart(4, "0"),
      "HHmm",
      {
        zone: timezone,
      }
    ).set({
      year: zonedTime.year,
      month: zonedTime.month,
      day: zonedTime.day,
    });
    const end =
      Number(timeSlot.end) === 2400 ? endRaw.plus({ days: 1 }) : endRaw;

    return (
      start.isValid && end.isValid && zonedTime >= start && zonedTime <= end
    );
  });
};

/**
 * A utility function that calculates a list of valid, schedulable date options
 * based on the merchant's business hours, timezone constraints, and order lead time.
 *
 * @param props - Configuration properties for determining date availability.
 * @returns {ScheduleDateType[]} An array of valid schedulable dates with localized display values.
 */
export const getSchedulableDateOptions = (
  scheduleDateRange: number,
  businessHours: Hours | undefined,
  timezone: string,
  t: TFunction,
  leadTime?: number,
  daypartMenu?: DayPart | null
): ScheduleDateType[] => {
  const today = DateTime.now().setZone(timezone);
  const dateOptions: ScheduleDateType[] = [];

  for (let i = 0; i <= scheduleDateRange; i++) {
    const date = today.plus({ days: i });
    const dayName = daysOfWeek[date.weekday % 7] as keyof Hours;
    const formattedDate = date.toFormat("MMM d");

    const businessHoursForDay = getBusinessHoursOfDay(
      businessHours,
      dayName,
      daypartMenu
    );

    // Guard Clause 1: Skip if there are no business hours configured for this day
    if (!businessHoursForDay || businessHoursForDay.length === 0) {
      continue;
    }

    // Guard Clause 2: Skip if the order cannot be scheduled within these hours
    const isValidSlot = canScheduleOrder(
      businessHoursForDay,
      today,
      timezone,
      leadTime,
      i === 0
    );
    if (!isValidSlot) {
      continue;
    }

    // Determine localized display labels
    if (i === 0) {
      dateOptions.push({
        displayValue: t("today"),
        dateObject: date.toJSDate(),
      });
    } else if (i === 1) {
      dateOptions.push({
        displayValue: t("tomorrowDate", { formattedDate }),
        dateObject: date.toJSDate(),
      });
    } else {
      dateOptions.push({
        displayValue: formattedDate,
        dateObject: date.toJSDate(),
      });
    }
  }

  return dateOptions;
};

export const getBusinessHoursOfDay = (
  businessHours: any,
  dayName: string,
  daypartMenu?: DayPart | null
) => {
  const businessHoursForDay = businessHours?.[dayName] || [];
  if (!daypartMenu) return businessHoursForDay;

  const { days: dpmDays, time: dpmTime } = daypartMenu || {};

  if (!dpmDays?.map((d) => d.toLowerCase()).includes(dayName) || !dpmTime)
    return [];

  const daypartMenuHours = getDayPartMenuHours(businessHoursForDay, dpmTime);

  return daypartMenuHours.map((slot: any) => ({
    start: slot.start.toString(),
    end: slot.end.toString(),
  }));
};

export const getDayPartMenuHours = (businessHoursForDay: any, dpmTime: any) => {
  const { startTime: start, endTime: end } = dpmTime;

  // Check if the menu time spans across midnight (end time is smaller than start time)
  if (end <= start) {
    // Handle times that span to next day (e.g., 22:00 to 02:00)
    const menuHours = businessHoursForDay.flatMap((slot: any) => {
      const results = [];

      // Check intersection with the time from start to end of day (2400)
      if (slot.end >= start) {
        const startTime = Math.max(slot.start, start);
        const endTime = slot.end;
        if (startTime < endTime) {
          results.push({ start: startTime, end: endTime });
        }
      }

      // Check intersection with the time from start of day (0000) to end
      if (slot.start <= end) {
        const startTime = slot.start;
        const endTime = Math.min(slot.end, end);
        if (startTime < endTime) {
          results.push({ start: startTime, end: endTime });
        }
      }

      return results;
    });

    return menuHours;
  } else {
    // Normal case where end time is after start time (same day)
    const menuHours = businessHoursForDay.map((slot: any) => {
      const startTime = Math.max(slot.start, start);
      const endTime = Math.min(slot.end, end);
      return { start: startTime, end: endTime };
    });

    return menuHours.filter((slot: any) => slot.start < slot.end);
  }
};

export const canAcceptASAPOrders = (
  merchant: Merchant,
  isActiveDayPartMenu: boolean
) => {
  if (!isActiveDayPartMenu) return false;

  const now = new Date();
  const currentDateTime = DateTime.fromJSDate(now, {
    zone: merchant.timezone,
  });
  const merchantService = merchant.services.find(
    (s: Service) =>
      s.type === ServiceType.PICKUP || s.type === ServiceType.WEBSITE
  );
  const businessHours = merchantService?.hours;

  const isNowWithinBusinessHours = isWithinBusinessHours(
    currentDateTime,
    businessHours,
    merchant?.timezone
  );

  return isNowWithinBusinessHours;
};

export const isOrderSchedulingEnabled = (merchant: Merchant) => {
  if (!merchant || isEmpty(merchant.scheduledOrdering)) return false;

  if (!merchant.scheduledOrdering.enabled) return false;

  if (
    merchant.scheduledOrdering.maxDays === 0 ||
    isNil(merchant.scheduledOrdering.maxDays)
  )
    return false;

  return true;
};

export const canMerchantAcceptOrders = (
  merchant: Merchant,
  isDineIn: boolean,
  dayPartMenu: DayPart | null,
  t: TFunction
) => {
  const isActiveDayPartMenu = dayPartMenu?.activeNow ?? true;
  //isBusinessOpen checks if busines is accepting online orders
  const isOpen = isBusinessOpen(merchant, isDineIn, isActiveDayPartMenu);

  //If not restaurant merchante, return if business open or not
  if (!merchant.isRestaurant) return isOpen;

  //If business is not open return false
  if (!isOpen) return false;

  //check if scheduling order is enabled for merchant
  const isSchedulingEnabled = isOrderSchedulingEnabled(merchant);

  //check if ASAP orders can be placed for current menu based on hours and current time
  const acceptingASAPOrders = canAcceptASAPOrders(
    merchant,
    isActiveDayPartMenu
  );
  //If orderscheduling is disabled, then return if asap orders accepted or not
  if (!isSchedulingEnabled) {
    return acceptingASAPOrders;
  }

  //If order scheduling is enabled then get schedulable dates. Either ASAP orders should be acceptable or should be able to schedule order based on menu availability and business hours
  const merchantService = merchant?.services.find(
    (s: Service) =>
      s.type === ServiceType.PICKUP || s.type === ServiceType.WEBSITE
  );
  const schedulableDateOptions = getSchedulableDateOptions(
    merchant?.scheduledOrdering.maxDays - 1,
    merchantService?.hours,
    merchant.timezone,
    t,
    merchant.leadTime,
    dayPartMenu
  );

  return acceptingASAPOrders || schedulableDateOptions?.length > 0;
};

export const getMerchantBusinessHours = (merchant: Merchant) => {
  const merchantService = merchant.services.find(
    (s: Service) => s.type === "PICKUP" || s.type === "WEBSITE"
  );

  return merchantService?.hours;
};

/**
 * Determines if the selected scheduled pickup time is no longer viable
 * because it is in the past or falls within the merchant's required lead time.
 *
 * @param scheduledTimeState - The user-selected scheduled time as an ISO string or Date object.
 * @param timezone - The IANA timezone string of the merchant (e.g., "America/New_York").
 * @param leadTimeMinutes - The merchant's required preparation buffer in minutes.
 * @returns boolean - True if the scheduled time is before (now + leadTime), false otherwise.
 */
export const isPastSelectedPickupTime = (
  scheduledTimeState: Date | string,
  timezone: string,
  leadTimeMinutes: number = 0
): boolean => {
  if (!scheduledTimeState) {
    return false;
  }

  let scheduledDateTime: DateTime;

  if (scheduledTimeState instanceof Date) {
    scheduledDateTime =
      DateTime.fromJSDate(scheduledTimeState).setZone(timezone);
  } else {
    scheduledDateTime = DateTime.fromISO(scheduledTimeState).setZone(timezone);
  }

  // Safety check in case of an invalid date string/object
  if (!scheduledDateTime.isValid) {
    return false;
  }

  // Calculate the cutoff threshold: current time + lead time buffer, if any
  const cutoffTime = DateTime.now()
    .setZone(timezone)
    .plus({ minutes: leadTimeMinutes });

  return scheduledDateTime < cutoffTime;
};

interface TimeRange {
  start: string;
  end: string;
}

interface AvailableTimeOptionsParams {
  dateObject: Date | null | undefined;
  timezone: string;
  locale: string;
  timeRange: TimeRange[];
  leadTime: number;
  deliveryDelay: number;
}

interface TimeOption {
  displayValue: string;
  timeObject: Date;
}

/**
 * Generates a list of available pickup/delivery 15-minute time slots based on business hours,
 * required lead times, and the merchant's timezone.
 */
export const getAvailableTimeOptions = ({
  dateObject,
  timezone,
  locale,
  timeRange,
  leadTime,
  deliveryDelay,
}: AvailableTimeOptionsParams): TimeOption[] => {
  if (!dateObject || !timeRange || timeRange.length === 0) {
    return [];
  }

  const now = DateTime.now().setZone(timezone).setLocale(locale);
  const currentDayInMerchantZone = DateTime.fromJSDate(dateObject, {
    zone: timezone,
  });

  // If the selected day is in the past, no slots are available
  if (currentDayInMerchantZone.startOf("day") < now.startOf("day")) {
    return [];
  }

  const options: TimeOption[] = [];

  // Helper to round up to the next 15-minute interval
  const roundToNext15Minutes = (dt: DateTime): DateTime => {
    const remainder = dt.minute % 15;
    if (remainder === 0) return dt.startOf("minute");
    return dt.plus({ minutes: 15 - remainder }).startOf("minute");
  };

  timeRange.forEach(({ start, end }) => {
    const startTime = HHMMtoDate(
      String(start).padStart(4, "0"),
      timezone,
      dateObject
    );
    const endTime = HHMMtoDate(
      String(end).padStart(4, "0"),
      timezone,
      dateObject
    );

    // Base threshold: opening time + buffer delays
    let leadTimeThreshold = roundToNext15Minutes(
      startTime.plus({ minutes: leadTime + deliveryDelay })
    );

    // If the selected day is today and the slot's opening time has already passed,
    // calculate the threshold relative to 'now' instead of the opening time.
    if (currentDayInMerchantZone.hasSame(now, "day") && startTime < now) {
      leadTimeThreshold = roundToNext15Minutes(
        now.plus({ minutes: leadTime + deliveryDelay })
      );
    }

    // Generate intervals of 15 minutes up to the end time
    for (
      let time = leadTimeThreshold;
      time <= endTime;
      time = time.plus({ minutes: 15 })
    ) {
      options.push({
        displayValue: time
          .setLocale(locale)
          .toLocaleString(DateTime.TIME_SIMPLE),
        timeObject: time.toJSDate(),
      });
    }
  });

  return options;
};

/**
 * High-level orchestrator that extracts merchant contexts, evaluates operation hours,
 * handles delivery lags, and generates chronological order windows.
 */
export const getMerchantAvailableTimeOptions = ({
  dateObject,
  merchant,
  orderType,
  locale,
  selectedDayPart,
  deliveryTime,
  pickupTime,
}: {
  dateObject: Date | null | undefined;
  merchant: Merchant;
  orderType: OrderType;
  locale: string;
  selectedDayPart?: DayPart | null;
  deliveryTime?: string | number | Date | null;
  pickupTime?: string | number | Date | null;
}) => {
  if (!dateObject || !merchant) return [];

  const { timezone, leadTime, isRestaurant, businessHours } = merchant;

  // Flatten operational hours across varying verticals
  const currentHours = isRestaurant
    ? getMerchantBusinessHours(merchant)
    : businessHours?.hours;

  let deliveryDelay = 0;
  if (orderType === OrderType.DELIVERY && deliveryTime && pickupTime) {
    const deliveryTimeDate = new Date(deliveryTime).getTime();
    const pickUpTimeDate = new Date(pickupTime).getTime();
    deliveryDelay = Math.round(
      (deliveryTimeDate - pickUpTimeDate) /
        TIME_CONSTANTS.MILLISECONDS_PER_SECOND /
        TIME_CONSTANTS.MINUTES_PER_HOUR
    );
  }

  const dayIndex = DateTime.fromJSDate(dateObject, { zone: timezone }).weekday;
  const name = daysOfWeek[dayIndex % 7];

  // Cull business hours profiles
  const timeRange = getBusinessHoursOfDay(currentHours, name, selectedDayPart);

  return getAvailableTimeOptions({
    dateObject,
    timezone,
    locale,
    timeRange,
    leadTime,
    deliveryDelay,
  });
};
